import React, { useState, useRef, useEffect } from "react";
import { Typography, Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import "./ShowMoreShowLess.scss";

interface ReadMoreTextProps {
  text: string;
  mode?: "char" | "word" | "line";
  limit?: number;
  lines?: number;
}

const renderMessageWithBold = (message: string) => {
  const boldRegex = /\*\*(.*?)\*\*/g;
  if (!message) return message;

  const parts = message.split(boldRegex);
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <strong key={index}>{part}</strong>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );
};

const ReadMoreText: React.FC<ReadMoreTextProps> = ({
  text,
  mode = "char",
  limit = 120,
  lines = 3,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);


  useEffect(() => {
    if (mode === "line" && textRef.current) {
      const isOverflowing =
        textRef.current.scrollHeight > textRef.current.clientHeight;
      setShowButton(isOverflowing);
    }
  }, [text, mode, lines]);

  if (!text) return null;

  const getTruncatedText = () => {
    if (expanded || mode === "line") return text;

    if (mode === "word") {
      const words = text.split(" ");
      if (words.length <= limit) return text;
      return words.slice(0, limit).join(" ") + "...";
    }

    if (text.length <= limit) return text;
    return text.slice(0, limit).trimEnd() + "...";
  };

  const shouldShowButtonJS = () => {
    if (mode === "line") return showButton;
    if (mode === "word") return text.split(" ").length > limit;
    return text.length > limit;
  };

  const toggleText = () => setExpanded((prev) => !prev);

  return (
    <Box className="readMoreLessWrapper">
      <Typography
        ref={textRef}
        className={`description ${mode === "line" && !expanded ? "line-clamp" : ""}`}
        sx={mode === "line" ? { "--lines": lines } : {}}
      >
        {renderMessageWithBold(getTruncatedText())}
      </Typography>

      {shouldShowButtonJS() && (
        <Button className="readMoreToggle" onClick={toggleText}>
          {expanded ? t("Read_Less") : t("Read_More")}
        </Button>
      )}
    </Box>
  );
};

export default ReadMoreText;